import React from 'react';
import { Card } from 'antd';
import { $createParagraphNode, $createTextNode, $getRoot } from 'lexical';
import PropTypes from 'prop-types';

import { ListItemNode, ListNode } from '@lexical/list';
import { MarkNode } from '@lexical/mark';
import { OverflowNode } from '@lexical/overflow';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { CheckListPlugin } from '@lexical/react/LexicalCheckListPlugin';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';

import CollapsiblePlugin from './plugins/CollapsiblePlugin';
import { CollapsibleContainerNode } from './plugins/CollapsiblePlugin/CollapsibleContainerNode';
import { CollapsibleContentNode } from './plugins/CollapsiblePlugin/CollapsibleContentNode';
import { CollapsibleTitleNode } from './plugins/CollapsiblePlugin/CollapsibleTitleNode';
import { MaxLengthPlugin } from './plugins/MaxLengthPlugin';
import ToolbarPlugin from './plugins/ToolbarPlugin';
import theme from './theme';

import './styles.scss';

function InitialContentPlugin({ content }) {
    const [editor] = useLexicalComposerContext();
    const [isInitialized, setIsInitialized] = React.useState(false);

    React.useEffect(() => {
        // Only run once on mount to set initial content
        if (!isInitialized && content) {
            editor.update(() => {
                // Check if content is JSON (Lexical editor state) or plain text
                try {
                    JSON.parse(content);
                    // Content is already loaded via editorState config, nothing to do
                } catch (e) {
                    // Content is plain text, need to set it manually
                    const paragraph = $createParagraphNode();
                    const text = $createTextNode(content);
                    paragraph.append(text);
                    $getRoot().clear().append(paragraph);
                }
            });
            setIsInitialized(true);
        }
    }, [editor, content, isInitialized]);

    return null;
}

InitialContentPlugin.propTypes = {
    content: PropTypes.string
};

export default function NotesEditor({
    content,
    onChange,
    placeholder,
    contentToAppend,
    SubmitButton,
    CancelButton,
    isTemplateModal = false,
    isReadOnly = false,
    showMinimalToolbar = false,
    maxLength = 2000
}) {
    // Prepare initial editor state from content prop
    const getInitialEditorState = () => {
        if (content) {
            // Check if content is JSON (Lexical editor state)
            try {
                JSON.parse(content);
                return content;
            } catch (e) {
                // If not JSON, it's plain text - return undefined and let ToolbarPlugin handle it
                return undefined;
            }
        }
        return undefined;
    };

    const editorConfig = {
        namespace: 'Journal Note',
        nodes: [
            ListNode,
            ListItemNode,
            OverflowNode,
            MarkNode,
            CollapsibleContainerNode,
            CollapsibleContentNode,
            CollapsibleTitleNode
        ],
        editable: true,
        onError(error) {
            throw error;
        },
        theme,
        editorState: getInitialEditorState()
    };

    const handleEditorChange = (editorState) => {
        const json = editorState.toJSON();

        if (json?.root.direction) {
            const jsonString = JSON.stringify(json);
            onChange(jsonString);
        } else {
            onChange('');
        }
    };

    return (
        <Card className="notes-editor">
            <LexicalComposer initialConfig={editorConfig}>
                <div className="editor-container">
                    <ToolbarPlugin
                        contentToAppend={contentToAppend}
                        isReadOnly={isReadOnly}
                        isTemplateModal={isTemplateModal}
                        showMinimalToolbar={showMinimalToolbar}
                    />
                    <div className="editor-inner">
                        <RichTextPlugin
                            contentEditable={
                                <ContentEditable
                                    className="editor-input"
                                    aria-placeholder={placeholder}
                                    onChange={handleEditorChange}
                                    placeholder={
                                        <div className="editor-placeholder">{placeholder}</div>
                                    }
                                />
                            }
                            ErrorBoundary={LexicalErrorBoundary}
                        />
                        <InitialContentPlugin content={content} />
                        <ListPlugin />
                        <CheckListPlugin />
                        {maxLength && <MaxLengthPlugin maxLength={maxLength} />}
                        <HistoryPlugin />
                        <AutoFocusPlugin />
                        <OnChangePlugin onChange={handleEditorChange} />
                        {!showMinimalToolbar && <CollapsiblePlugin />}
                    </div>
                </div>
            </LexicalComposer>
            {SubmitButton && (
                <div className="editor-submit">
                    <CancelButton />
                    <SubmitButton />
                </div>
            )}
        </Card>
    );
}

NotesEditor.propTypes = {
    onChange: PropTypes.func,
    content: PropTypes.any,
    placeholder: PropTypes.string,
    contentToAppend: PropTypes.any,
    SubmitButton: PropTypes.any,
    CancelButton: PropTypes.any,
    isTemplateModal: PropTypes.bool,
    selectedTemplate: PropTypes.string,
    isReadOnly: PropTypes.bool,
    showMinimalToolbar: PropTypes.bool,
    maxLength: PropTypes.number
};
